import { type Placement } from '@floating-ui/react';

/**
 * A floating-ui position reference. A live element resolves its own rect; a
 * detached anchor (e.g. a calendar block that scrolled out of the DOM) can
 * fall back to a captured rect via `getBoundingClientRect`.
 */
export interface HoverPopoverAnchorReference {
  getBoundingClientRect: () => DOMRect;
  contextElement?: Element;
}

/** Build an anchor reference from a live element, with a captured-rect fallback. */
export const anchorReferenceFromElement = (
  anchorEl: HTMLElement,
): HoverPopoverAnchorReference => {
  const capturedRect = anchorEl.getBoundingClientRect();
  return {
    contextElement: anchorEl,
    getBoundingClientRect: () =>
      anchorEl.isConnected ? anchorEl.getBoundingClientRect() : capturedRect,
  };
};

/** Hover-intent timing. Defaults match the calendar's original feel. */
export interface HoverPopoverTiming {
  /** Rest-before-open delay (ms). Default 450. */
  openDelayMs?: number;
  /** Grace period before close on mouse-leave (ms). Default 100. */
  closeDelayMs?: number;
}

export const HOVER_POPOVER_OPEN_MS = 450;
export const HOVER_POPOVER_CLOSE_MS = 100;
/** Open/close animation duration (ms). */
export const HOVER_POPOVER_REST_MS = 120;

export type { Placement };
